/* eslint-disable no-prototype-builtins */
import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormLabel from '@material-ui/core/FormLabel';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { useTranslate, useNotify } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import { convertTime, getZ3PConfigValue } from '../../helper';
import ClientProfileSettings from './ClientProfileSettings';
import 'rc-slider/assets/index.css';
import { apiGet, apiPut } from '../../brainApi';
import WelcomeEmailPreview from './WelcomeEmailPreview';

const Slider = require('rc-slider');

const { Range } = Slider;

const BusinessConfiguration = () => {
  const [
    clientConfigurationSettings,
    setClientConfigurationSettings,
  ] = useState({});
  const [updatedConfiguration, setUpdatedConfiguration] = useState({});
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [
    showDetailingPriceSheetInput,
    setShowDetailingPriceSheetInput,
  ] = useState(true);
  const translate = useTranslate();
  const notify = useNotify();

  const defaults = {
    default_arrival_time: '9:00 AM',
    default_departure_time: '5:00 PM',
    time_range_min: 9,
    time_range_max: 17,
    time_range_marks: '9,12,17',
    calendar_color: 'serviceVehicle',
    has_workplace_service: false,
    is_using_waitlist: false,
    is_performing_recalls: false,
    lead_time_availability_windows_number_of_days: 0,
  };

  const loadConfiguration = () => {
    apiGet(`/business-z3p-configuration`).then((clientConfiguration) => {
      const defaultsToUpdate = {};
      const defaultsThatCanBeConfigured = Object.keys(defaults).filter((key) =>
        clientConfiguration.hasOwnProperty(key),
      );
      defaultsThatCanBeConfigured.forEach((key) => {
        if (
          !clientConfiguration[key].value &&
          clientConfiguration[key].value !== 0
        ) {
          clientConfiguration[key].value = defaults[key];
          defaultsToUpdate[key] = defaults[key];
        }
      });
      setClientConfigurationSettings(clientConfiguration);
      setUpdatedConfiguration(defaultsToUpdate);
      // Only show the detailing price sheet field if it is not already set
      if (clientConfiguration.detailing_price_sheet?.value)
        setShowDetailingPriceSheetInput(false);
    });
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const categorizedClientConfigurationSettings = {};
  if (Object.keys(clientConfigurationSettings).length > 0) {
    Object.entries(clientConfigurationSettings).forEach(([key, value]) => {
      if (categorizedClientConfigurationSettings[value.category]) {
        categorizedClientConfigurationSettings[value.category].push({
          [key]: value,
        });
      } else {
        categorizedClientConfigurationSettings[value.category] = [
          { [key]: value },
        ];
      }
    });
  }

  const convertStringToArray = (value) => {
    return value.split(',').filter((item) => item !== '');
  };

  const formatTimeMarks = (marksString) => {
    if (!marksString) return null;
    const marks = convertStringToArray(marksString);
    const marksObject = {};
    marks.forEach((mark) => {
      if (mark === '12') {
        marksObject[mark] = 'Noon';
      } else {
        marksObject[mark] = convertTime(mark, 'ampm');
      }
    });
    return marksObject;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    const configurationToUpdate = updatedConfiguration;
    ['time_range_min', 'time_range_max'].forEach((key) => {
      if (Object.keys(configurationToUpdate).includes(key)) {
        configurationToUpdate[key] = convertTime(
          configurationToUpdate[key],
          'militaryNumber',
        );
      }
    });
    [
      'default_arrival_time',
      'default_departure_time',
      'lead_time_availability_windows_time_of_day',
    ].forEach((key) => {
      if (Object.keys(configurationToUpdate).includes(key)) {
        configurationToUpdate[key] = convertTime(
          configurationToUpdate[key],
          'ampm',
        );
      }
    });
    const schedulingSystem = getZ3PConfigValue('scheduling_system');
    if (schedulingSystem === 'availability_windows') {
      // If a field is not set, it should fall back on its existing value
      let time_of_day =
        configurationToUpdate.lead_time_availability_windows_time_of_day;
      let num_days =
        configurationToUpdate.lead_time_availability_windows_number_of_days;
      if (
        !configurationToUpdate.hasOwnProperty(
          'lead_time_availability_windows_time_of_day',
        )
      ) {
        time_of_day =
          clientConfigurationSettings.lead_time_availability_windows_time_of_day
            .value;
      }
      if (
        !configurationToUpdate.hasOwnProperty(
          'lead_time_availability_windows_number_of_days',
        )
      ) {
        num_days =
          clientConfigurationSettings
            .lead_time_availability_windows_number_of_days.value;
      }
      // If either field is null, set both to null
      if (['', null].includes(time_of_day) || num_days === null) {
        configurationToUpdate.lead_time_availability_windows_time_of_day = null;
        configurationToUpdate.lead_time_availability_windows_number_of_days = null;
      }
    }
    if (Object.keys(configurationToUpdate).includes('time_range_marks')) {
      configurationToUpdate.time_range_marks = formatTimeMarks(
        configurationToUpdate.time_range_marks,
      );
    }
    const arrayUpdatedKeys = Object.keys(configurationToUpdate).filter(
      (key) => clientConfigurationSettings[key].data_type === 'array',
    );
    if (arrayUpdatedKeys && arrayUpdatedKeys.length > 0) {
      arrayUpdatedKeys.forEach((key) => {
        if (typeof configurationToUpdate[key] === 'string') {
          configurationToUpdate[key] = convertStringToArray(
            configurationToUpdate[key],
          );
        }
      });
    }
    setIsSaving(true);
    apiPut('/z3p-configuration', configurationToUpdate).then((json) => {
      setIsSaving(false);
      if (json.Status === 'Failure') {
        notify(`Error: ${json.Message}`, 'warning');
      } else {
        notify('Settings saved successfully.', 'info');
        // Re-fetch latest config from the server to reflect saved values without a full page reload
        loadConfiguration();
      }
    });
  };

  return (
    <form>
      <h3>
        Business Configuration Settings
        {clientConfigurationSettings.customer_facing_name &&
        clientConfigurationSettings.customer_facing_name.value
          ? ` for ${clientConfigurationSettings.customer_facing_name.value}`
          : ''}
      </h3>
      {Object.entries(categorizedClientConfigurationSettings).map(
        ([category, categorySettings]) => {
          return (
            <div>
              <h4 style={{ margin: '20px 0 10px 0' }}>
                {translate(
                  `resources.business-configuration.headers.${category}`,
                )}
              </h4>
              {category === 'email' && (
                <div
                  style={{ fontSize: '.9rem', cursor: 'pointer' }}
                  onClick={() => setShowEmailSettings(!showEmailSettings)}
                >
                  {showEmailSettings ? (
                    <KeyboardArrowDownIcon />
                  ) : (
                    <KeyboardArrowRightIcon />
                  )}
                  <span style={{ verticalAlign: 'super' }}>
                    Click to see email configuration settings
                  </span>
                </div>
              )}
              <Grid container>
                <Grid item xs={12} sm={12} md={6}>
                  {category === 'email' && showEmailSettings && (
                    <WelcomeEmailPreview
                      backgroundColor={
                        clientConfigurationSettings.email_background_color.value
                      }
                      buttonColor={
                        clientConfigurationSettings.email_button_color.value
                      }
                      clientUrl={clientConfigurationSettings.client_url.value}
                      email={
                        clientConfigurationSettings.customer_facing_email.value
                      }
                      phoneRaw={
                        clientConfigurationSettings.customer_facing_phone_raw
                          .value
                      }
                      phone={
                        clientConfigurationSettings.customer_facing_phone.value
                      }
                      clientName={
                        clientConfigurationSettings.customer_facing_name.value
                      }
                      address={
                        clientConfigurationSettings.customer_facing_address
                          .value
                      }
                      welcomeMessage={
                        clientConfigurationSettings.email_welcome_message.value
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  {category === 'email' &&
                    showEmailSettings &&
                    categorySettings.map((setting) => {
                      return (
                        <div>
                          <br />
                          <ClientProfileSettings
                            setting={setting}
                            clientConfigurationSettings={
                              clientConfigurationSettings
                            }
                            setClientConfigurationSettings={
                              setClientConfigurationSettings
                            }
                            updatedConfiguration={updatedConfiguration}
                            setUpdatedConfiguration={setUpdatedConfiguration}
                            errors={errors}
                            setErrors={setErrors}
                            showErrors={showErrors}
                            setShowErrors={setShowErrors}
                            showDetailingPriceSheetInput={
                              showDetailingPriceSheetInput
                            }
                          />
                        </div>
                      );
                    })}
                </Grid>
              </Grid>
              {category !== 'email' &&
                categorySettings.map((setting) => {
                  return (
                    <ClientProfileSettings
                      setting={setting}
                      clientConfigurationSettings={clientConfigurationSettings}
                      setClientConfigurationSettings={
                        setClientConfigurationSettings
                      }
                      updatedConfiguration={updatedConfiguration}
                      setUpdatedConfiguration={setUpdatedConfiguration}
                      errors={errors}
                      setErrors={setErrors}
                      showErrors={showErrors}
                      setShowErrors={setShowErrors}
                      showDetailingPriceSheetInput={
                        showDetailingPriceSheetInput
                      }
                    />
                  );
                })}
              {category === 'app-schedule' && (
                <div>
                  <br />
                  <p style={{ fontSize: '.8rem' }}>
                    View-only preview of the time range slider in the booking
                    app
                  </p>
                  <Range
                    style={{ margin: '0 20px', maxWidth: '800px' }}
                    min={
                      typeof clientConfigurationSettings.time_range_min
                        .value === 'string'
                        ? convertTime(
                            clientConfigurationSettings.time_range_min.value,
                            'militaryNumber',
                          )
                        : clientConfigurationSettings.time_range_min.value
                    }
                    max={
                      typeof clientConfigurationSettings.time_range_max
                        .value === 'string'
                        ? convertTime(
                            clientConfigurationSettings.time_range_max.value,
                            'militaryNumber',
                          )
                        : clientConfigurationSettings.time_range_max.value
                    }
                    step={0.5}
                    value={[
                      convertTime(
                        clientConfigurationSettings.default_arrival_time.value,
                        'militaryNumber',
                      ),
                      convertTime(
                        clientConfigurationSettings.default_departure_time
                          .value,
                        'militaryNumber',
                      ),
                    ]}
                    marks={
                      clientConfigurationSettings.time_range_marks.value &&
                      typeof clientConfigurationSettings.time_range_marks
                        .value === 'string'
                        ? formatTimeMarks(
                            clientConfigurationSettings.time_range_marks.value,
                          )
                        : clientConfigurationSettings.time_range_marks.value ||
                          false
                    }
                    disabled
                  />
                </div>
              )}
            </div>
          );
        },
      )}
      <br />
      {showErrors && (
        <div>
          <FormLabel style={{ color: 'red' }}>
            This form has errors. Please correct{' '}
            {errors
              .map((error) =>
                translate(`resources.business-configuration.fields.${error}`),
              )
              .join(', ')}{' '}
            and then submit again.
          </FormLabel>
          <br />
          <br />
        </div>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={(event) => handleSubmit(event)}
        disabled={isSaving}
      >
        {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
      </Button>
    </form>
  );
};

export default BusinessConfiguration;
